import { BlocCoordinator } from "@lib/bloc/BlocCoordinator"
import { compare, SortOrder } from "@lib/Comparable"
import { AssetCategory } from "@model/assets/AssetCategory"
import { AssetClass } from "@model/assets/AssetClass"
import { AssetMake } from "@model/assets/AssetMake"
import { AssetModel } from "@model/assets/AssetModel"
import { AssetType } from "@model/assets/AssetType"
import { Company } from "@model/company/Company"
import { CompanyAlias } from "@model/company/CompanyAlias"
import { ThresholdSettings } from "@model/company/CompanySettings"
import { District } from "@model/district/District"
import { Filter, FilterValue, isFilterEmptyOrContainsValue } from "@model/filters/Filter"
import { FilterPreset, FilterPresetForm, FilterPresetType } from "@model/filters/FilterPresets"
import { Group } from "@model/group/Group"
import { labelForServiceCode, ServiceCode, ServiceSchedule } from "@model/serviceRequests/ServiceRequest"
import { Site } from "@model/site/Site"
import { WorkRequestFilter } from "@model/workRequests/WorkRequest"
import { CompanyBloc, CompanyState } from "@state/company/CompanyBloc"
import {
    FilterOverlayBlocCoordinator,
    FilterOverlayBlocCoordinatorMixin,
} from "@state/filters/FilterOverlayBlocCoordinatorMixin"
import { StatusBloc } from "@state/StatusBloc"
import { UserBloc } from "@state/user/UserBloc"
import { UserPreferencesBloc, UserPreferencesState } from "@state/UserPreferencesBloc"
import {
    WorkRequestsFilterOverlayBloc,
    WorkRequestsFilterOverlayState,
} from "@state/workRequests/WorkRequestsFilterOverlayBloc"
import { WorkRequestsTableBloc } from "@state/workRequests/WorkRequestsTableBloc"

type Dependencies = [WorkRequestsFilterOverlayState, UserPreferencesState, CompanyState]
type StateSelection = WorkRequestsFilterOverlayState & {
    parentCompany: Company
    subCompanies: Company[]
    makes: AssetMake[]
    models: AssetModel[]
    types: AssetType[]
    categories: AssetCategory[]
    classes: AssetClass[]
    districts: District[]
    subDistricts: District[]
    units: District[]
    groups: Group[]
    aliases: Record<CompanyAlias, string>
    sites: Site[]
    filterPresets: FilterPreset<WorkRequestFilter>[]
    serviceCodes: ServiceCode[]
    serviceSchedules: ServiceSchedule[]
    upcomingThresholds: ThresholdSettings
}

export class WorkRequestsFilterOverlayViewModel
    extends BlocCoordinator<Dependencies, StateSelection>
    implements FilterOverlayBlocCoordinator<WorkRequestFilter>
{
    private filterOverlayMixin: FilterOverlayBlocCoordinatorMixin<WorkRequestFilter>

    constructor(
        filterOverlayBloc: WorkRequestsFilterOverlayBloc,
        userPreferencesBloc: UserPreferencesBloc,
        workRequestsTableBloc: WorkRequestsTableBloc,
        companyBloc: CompanyBloc,
        statusBloc: StatusBloc,
        userBloc: UserBloc
    ) {
        super([filterOverlayBloc, userPreferencesBloc, companyBloc])

        this.filterOverlayMixin = new FilterOverlayBlocCoordinatorMixin(
            FilterPresetType.WorkRequest,
            filterOverlayBloc,
            workRequestsTableBloc,
            userPreferencesBloc,
            statusBloc,
            userBloc
        )
    }

    transform = ([filterOverlayState, userPreferencesState, companyState]: Dependencies): StateSelection => {
        const filteredDistricts = companyState.districts.filter((it) =>
            isFilterEmptyOrContainsValue(filterOverlayState.filters[WorkRequestFilter.SubCompany], [it.companyId])
        )

        const filteredSubDistricts = companyState.subDistricts.filter(
            (it) =>
                isFilterEmptyOrContainsValue(filterOverlayState.filters[WorkRequestFilter.SubCompany], [
                    it.companyId,
                ]) &&
                isFilterEmptyOrContainsValue(filterOverlayState.filters[WorkRequestFilter.District], [it.parentId ?? 0])
        )

        const filteredUnits = companyState.units.filter(
            (it) =>
                filteredSubDistricts.find((subDistrict) => subDistrict.id === it.parentId) !== undefined &&
                isFilterEmptyOrContainsValue(filterOverlayState.filters[WorkRequestFilter.SubDistrict], [
                    it.parentId ?? 0,
                ])
        )

        return {
            ...filterOverlayState,
            aliases: companyState.aliases,
            parentCompany: {
                id: companyState.companyId,
                name: companyState.companyName,
                parentId: -1,
                logoUrl: companyState.companyLogoUrl,
            },
            subCompanies: companyState.subCompanies,
            sites: companyState.sites,
            districts: filteredDistricts,
            subDistricts: filteredSubDistricts,
            units: filteredUnits,
            groups: companyState.groups.filter((it) =>
                isFilterEmptyOrContainsValue(filterOverlayState.filters[WorkRequestFilter.SubCompany], [it.companyId])
            ),
            makes: companyState.assetMakes,
            models: companyState.assetModels.filter((it) =>
                isFilterEmptyOrContainsValue(filterOverlayState.filters[WorkRequestFilter.Make], [it.makeId])
            ),
            types: companyState.assetTypes,
            categories: companyState.assetCategories.filter((it) =>
                isFilterEmptyOrContainsValue(filterOverlayState.filters[WorkRequestFilter.AssetType], [it.typeId])
            ),
            classes: companyState.assetClasses.filter((it) =>
                isFilterEmptyOrContainsValue(filterOverlayState.filters[WorkRequestFilter.Category], [it.categoryId])
            ),
            serviceCodes: Object.values(companyState.serviceCodes)
                .filter((it) => it.associatedWithWorkRequest)
                .sort((a, b) => compare(labelForServiceCode(a), labelForServiceCode(b), SortOrder.Asc)),
            serviceSchedules: Object.values(companyState.serviceSchedules)
                .filter((it) => it.associatedWithWorkRequest)
                .sort((a, b) => compare(a.name, b.name, SortOrder.Asc)),
            preset: filterOverlayState.preset,
            filterPresets: userPreferencesState.filterPresets.workRequests.sort((a, b) =>
                compare(a.name, b.name, SortOrder.Asc)
            ),
            upcomingThresholds: companyState.settings.thresholds,
        }
    }

    toggleOverlayVisibility = () => this.filterOverlayMixin.toggleOverlayVisibility()

    updateFilters = (filters: Record<WorkRequestFilter, Filter<WorkRequestFilter>>) =>
        this.filterOverlayMixin.updateFilters(filters)

    updateFilter = (type: WorkRequestFilter, value: FilterValue[]) => this.filterOverlayMixin.updateFilter(type, value)

    resetFilters = () => this.filterOverlayMixin.resetFilters()

    applyFilters = async () => this.filterOverlayMixin.applyFilters()

    filterPresetSelected = (id: number) => this.filterOverlayMixin.filterPresetSelected(id)

    showCreatePreset = () => this.filterOverlayMixin.showCreatePreset()

    hideCreatePreset = () => this.filterOverlayMixin.hideCreatePreset()

    createPreset = (form: FilterPresetForm) => this.filterOverlayMixin.createPreset(form)

    updatePresetFilters = () => this.filterOverlayMixin.updatePresetFilters()

    updatePresetForm = (preset: FilterPreset<WorkRequestFilter>, form: FilterPresetForm) =>
        this.filterOverlayMixin.updatePresetForm(preset, form)

    deletePreset = (preset: FilterPreset<WorkRequestFilter>) => this.filterOverlayMixin.deletePreset(preset)

    showManagePresets = () => this.filterOverlayMixin.showManagePresets()

    hideManagePresets = () => this.filterOverlayMixin.hideManagePresets()

    isSearchFilterValid = () => this.filterOverlayMixin.isSearchFilterValid()

    isSecondaryFilterValid = () => this.filterOverlayMixin.isSecondaryFilterValid()

    IsInMaintenance25Revision(): boolean {
        return this.filterOverlayMixin.IsInMaintenance25Revision()
    }
}
